import classNames from 'classnames';
import { useMemo } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Adventure, useAdventure } from '~/components/Adventure/Adventure.hooks';
import { AdventureCard } from '~/components/Adventure/Card/v2/AdventureCard';
import { breakpoints, useBreakpoint } from '~/components/Breakpoint/Breakpoint.hook';
import { Carousel, CarouselProps } from '~/components/Carousel';
import styles from './AdventureRewardCarousel.module.scss';

const oddLengthCarouselProps: CarouselProps = {
  autoplay: { delay: 4000, disableOnInteraction: false },
  pagination: true,
  shadow: true,
  breakpoints: {
    0: {
      slidesPerView: 1,
      spaceBetween: 10,
    },
    [breakpoints.medium]: {
      slidesPerGroup: 3,
      slidesPerView: 3,
      spaceBetween: 21,
    },
  },
};

const evenLengthCarouselProps: CarouselProps = {
  autoplay: { delay: 4000, disableOnInteraction: false },
  pagination: true,
  shadow: true,
  breakpoints: {
    0: {
      slidesPerView: 1,
      spaceBetween: 10,
    },
    [breakpoints.medium]: {
      slidesPerGroup: 2,
      slidesPerView: 2,
      spaceBetween: 20,
    },
  },
};

const carouselPropsMap: Record<number, CarouselProps> = {
  1: {
    shadow: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
  },
};

export type AdventureRewardCarouselProps = Omit<CarouselProps, 'id'> & Pick<Adventure, 'id'>;

export const AdventureRewardCarousel = ({
  id,
  className,
  ...props
}: AdventureRewardCarouselProps) => {
  const { data: { rewardList = [] } = {} } = useAdventure(id, { staleTime: 10 * 1000 });
  const lotteryRewards = useMemo(
    () => rewardList.filter((each) => each.rewardType === 'LOTTERY_NFT'),
    [rewardList]
  );
  const breakpoint = useBreakpoint();
  const isEven = lotteryRewards.length % 2 === 0;

  const carouselProps = useMemo(() => {
    if (carouselPropsMap[lotteryRewards.length]) return carouselPropsMap[lotteryRewards.length];

    return isEven ? evenLengthCarouselProps : oddLengthCarouselProps;
  }, [isEven, lotteryRewards.length]);

  const cardVariant = useMemo(() => {
    if (lotteryRewards.length === 1) return 'large';
    return !isEven && lotteryRewards.length > 2 ? 'small' : 'medium';
  }, [isEven, lotteryRewards.length]);

  return (
    <Carousel
      className={classNames(styles.root, className)}
      {...carouselProps}
      {...props}
      key={`${breakpoint}-${lotteryRewards.length}`}>
      {lotteryRewards.map((reward) => (
        <SwiperSlide key={reward.id}>
          <AdventureCard variant={cardVariant} {...reward} />
        </SwiperSlide>
      ))}
    </Carousel>
  );
};
